import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { App, Button, notification as antdNotification } from 'antd';
import { Bell, BellOff } from 'lucide-react';
import { useChatHub } from '@/hooks/useChatHub';
import { chatService } from '@/services/chatService';
import { notifyService } from '@/services/notifyService';
import { useAuthStore } from '@/store/authStore';

/**
 * Component global: mount ở MainLayout (mọi page).
 * - Connect SignalR hub /hubs/chat luôn luôn (không chỉ khi vào /chat)
 * - Nhận tin mới → hiện browser notification (nếu cho phép) + in-app toast fallback
 * - Update badge count ở sidebar
 * - Track permission state → hiện nút "Bật thông báo" khi chưa cấp quyền
 */
export function ChatNotifier() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, isAuthenticated } = useAuthStore();
  const { notification } = App.useApp();
  const [permissionState, setPermissionState] =
    useState<NotificationPermission | 'unsupported'>('default');

  // Check current permission on mount + khi focus
  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPermissionState('unsupported');
      return;
    }
    setPermissionState(Notification.permission);
    const onFocus = () => {
      if (typeof Notification !== 'undefined') setPermissionState(Notification.permission);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // SignalR hub mount toàn cục
  useChatHub({
    onMessageReceived: async (msg) => {
      const isFromMe = msg.senderId === user?.id;

      // Check user đang ở đúng phòng đó không (không notify nếu đang chat)
      const isOnThisRoomPage = location.pathname === `/chat/${msg.roomId}`
        && document.visibilityState === 'visible'
        && document.hasFocus();

      // Refresh unread badge + rooms list
      qc.invalidateQueries({ queryKey: ['chat-rooms'] });
      qc.invalidateQueries({ queryKey: ['chat-unread-count'] });

      // Refresh messages nếu đang mở phòng đó
      if (isOnThisRoomPage) {
        qc.invalidateQueries({ queryKey: ['chat-messages', msg.roomId] });
      }

      if (isFromMe || isOnThisRoomPage) return;

      // Lấy tên phòng
      let roomName = 'Tin nhắn mới';
      try {
        const rooms = await chatService.listRooms();
        const room = rooms.find((r: any) => r.id === msg.roomId);
        if (room) {
          roomName = room.name
            ?? (room.type === 'DIRECT'
                ? (room.members?.find((m: any) => m.userId !== user?.id)?.fullName ?? 'Tin nhắn mới')
                : 'Tin nhắn mới');
        }
      } catch {}

      const senderName = msg.senderName ?? 'Ai đó';
      const preview = (msg.content?.length ?? 0) > 80
        ? msg.content.slice(0, 80) + '...'
        : (msg.content || '(đính kèm)');

      // Browser notification (nếu được phép)
      if (notifyService.isEnabled()) {
        notifyService.show(`${senderName} — ${roomName}`, {
          body: preview,
          tag: `chat-${msg.roomId}`,
          onClick: () => navigate(`/chat/${msg.roomId}`),
        });
      } else {
        // Fallback: in-app notification của Ant Design
        notification.info({
          message: `${senderName} — ${roomName}`,
          description: preview,
          placement: 'bottomRight',
          duration: 5,
          icon: <Bell size={18} style={{ color: '#6699CC' }} />,
          onClick: () => navigate(`/chat/${msg.roomId}`),
          style: { cursor: 'pointer' },
        });
      }

      // Rung máy trên mobile
      notifyService.vibrate(150);
    },
  });

  // Expose permission state via a hook helper
  return null;
}

/**
 * Nút nằm trong header cho user click để xin permission.
 * (Browser chặn requestPermission() tự động - cần user gesture)
 */
export function NotificationPermissionButton() {
  const [permission, setPermission] =
    useState<NotificationPermission | 'unsupported'>('default');
  const { message } = App.useApp();

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    setPermission(Notification.permission);
  }, []);

  if (permission === 'unsupported') return null;
  if (permission === 'granted') return null; // Đã bật, không hiển thị

  const handleClick = async () => {
    if (permission === 'denied') {
      message.warning({
        content: 'Thông báo đã bị chặn. Vui lòng vào cài đặt trình duyệt để cho phép.',
        duration: 6,
      });
      return;
    }

    const result = await notifyService.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      message.success('Đã bật thông báo. Bạn sẽ nhận được tin nhắn mới kể cả khi không ở trang chat.');
      // Test notification để user thấy ngay
      try {
        new Notification('HV-TaskFlow', {
          body: 'Thông báo đã được bật thành công!',
          icon: '/favicon.svg',
          tag: 'welcome',
        });
      } catch {}
    } else if (result === 'denied') {
      message.warning('Thông báo bị từ chối. Bạn có thể đổi cài đặt trong trình duyệt.');
    }
  };

  return (
    <Button
      type="default"
      size="small"
      icon={<BellOff size={13} style={{ color: '#faad14' }} />}
      onClick={handleClick}
      title="Bật thông báo để nhận tin nhắn khi không ở trang chat"
      style={{ fontSize: 12 }}
    >
      Bật thông báo
    </Button>
  );
}
